import { useContext } from 'react';

import { useSelector } from 'react-redux';
import { useResource } from 'react-resource-router';

import { CurrentDataContext } from 'src/app/current-data';
import { useAppData } from 'src/app/data';
import { NavDataContext } from 'src/app/nav-data';
import { useConsentBanner } from 'src/app/navigation-provider';
import { MenuItem } from 'src/components/navigation/src/types';
import urls from 'src/components/navigation/src/urls';
import { BANNER_HEIGHT } from 'src/constants/navigation';
import { useContainerNavigationProps } from 'src/hooks/use-container-navigation-props';
import { accountNavigationResource } from 'src/sections/account/account-navigation-resource';
import { getIsSiteMessageActive } from 'src/selectors/global-selectors';
import { settings } from 'src/settings';

const MAIN_NAV_MENU_ITEM_COUNT = 3;

type HorizontalNavigationItems = {
  mainItems: MenuItem[];
  secondaryItems: MenuItem[];
  settingsItems: MenuItem[];
};

export const useNavigationData = () => {
  const { workspace, project, repository } = useContext(CurrentDataContext);
  const { user, links, menuItems } = useContext(NavDataContext);
  const { data: accountNavData } = useResource(accountNavigationResource);
  // TODO: Add error handling
  const currentResource = repository || project || workspace;

  let isGlobalContext = false;
  const isBannerOpen = useSelector(getIsSiteMessageActive);
  const { features, navigationIsOpen } = useAppData();
  const { consentBannerHeight } = useConsentBanner()[0];
  const topOffset = isBannerOpen
    ? BANNER_HEIGHT + consentBannerHeight
    : consentBannerHeight;

  let containerHref;
  let containerLogo;
  let containerName;
  if (accountNavData && !currentResource) {
    // The accountNavData resource is cached and available even when the route
    // definition does not include the accountNavigationResource. This is likely
    // due to the fact that the resource key is static.
    isGlobalContext = true;
    containerName = accountNavData?.user?.display_name;
    containerHref = urls.ui.settings();
    containerLogo = '';
  } else {
    containerHref = currentResource?.links?.html?.href || '';
    // ^ Fall back to an empty string because some link components (e.g., RelativeLink) expect a string
    containerLogo = currentResource?.links?.avatar?.href;
    containerName = currentResource?.name;
  }

  let targetUserDisplayName;
  let targetUserUuid;
  if (accountNavData && !currentResource) {
    // Set target user info to point to the user as the account section is outside the
    // context of a workspace, to some extent. Workspace info is present on account section
    // requests to populate the global nav.
    targetUserDisplayName = accountNavData?.user?.display_name;
    targetUserUuid = accountNavData?.user?.uuid;
  } else {
    targetUserDisplayName = workspace?.name;
    targetUserUuid = workspace?.uuid;
  }

  return {
    isGlobalContext,
    useContainerNavigationProps,
    defaultNavigationOpen: navigationIsOpen,
    hasPride: !!features['bbc-pride-logo'],
    isPrivate: !!currentResource?.is_private,
    topOffset,

    // userPieces
    userAvatarUrl: user?.links.avatar.href,
    userDisplayName: user?.display_name,
    userUuid: user?.uuid,
    userEmail: user?.email,
    userWorkspaceId: user?.extra?.workspace_slug,

    // targetUserPieces, aka workspace info
    targetUserDisplayName,
    targetUserUuid,

    globalBitbucketActions: menuItems?.actions || [],
    horizontalNavigationItems: menuItems
      ? {
          mainItems: menuItems.main,
          secondaryItems: menuItems.secondary,
          settingsItems: menuItems.settings,
        }
      : // Add fake/empty menu items in order to stub out enough space for the real ones to render
        // once the data is fetched, otherwise they collapse into a "..." menu
        // This seems like it could be a bug in `AtlassianNavigation` from `@atlaskit/atlassian-navigation`
        // when the value of the `primaryItems` prop changes (and the # of items in the array increases)
        // @ts-ignore
        ({
          mainItems: Array(MAIN_NAV_MENU_ITEM_COUNT).fill({}),
          secondaryItems: [],
          settingsItems: [],
        } as HorizontalNavigationItems),

    // urls
    whatsNewUrl: links?.whatsNew.href,
    attributionsUrl: links?.attributions?.href,
    switchAccountUrl: links?.switch_account?.href,
    manageAccountUrl: links?.manage_account?.href,
    loginUrl: links?.login?.href,
    logoutUrl: links?.logout?.href,
    adminHubUrl: settings().ADMINHUB_BASE_URL,

    // Container menu header props
    containerHref,
    containerLogo,
    containerName,

    // current resources included in useLegacyReduxNavigationData
    currentWorkspace: workspace,
    currentRepository: repository,
    currentUser: user,

    // repo specific nav data
    ...(repository && {
      // including containerBitbucketActions here as project nav doesn't include it
      containerBitbucketActions: menuItems?.containerActions || undefined,
      repoName: repository.name,
      repoSlug: repository.slug,
    }),
  };
};
